import { useTranslation } from 'react-i18next'
import type { ProductCategory } from '@/api/productCatalog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * The two-level catalogue as a picker: a heading per category, its interests underneath.
 *
 * Retired entries are not offered — but one already on the lead is still shown, or editing an old
 * lead would quietly drop a tag nobody meant to remove. That is [alsoShow]'s only job.
 */
export function InterestPicker({
  categories,
  loading,
  selected,
  alsoShow,
  onToggle,
}: {
  categories: ProductCategory[]
  loading: boolean
  selected: Set<string>
  /** Interests already on the lead, so a retired one stays visible while it is still tagged. */
  alsoShow: { id: string; name: string; categoryId: string; categoryName: string }[]
  onToggle: (id: string, checked: boolean) => void
}) {
  const { t } = useTranslation()

  if (loading && categories.length === 0) {
    return (
      <div className="grid gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }

  const offered = new Set(categories.flatMap((category) => category.interests.map((i) => i.id)))
  const retiredButTagged = alsoShow.filter((interest) => !offered.has(interest.id))

  if (categories.length === 0 && retiredButTagged.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('leadForm.noCatalogue')}</p>
  }

  return (
    <div className="grid gap-4">
      {categories.map((category) => (
        <fieldset key={category.id} className="grid gap-2">
          <legend className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {category.name}
          </legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {category.interests.map((interest) => (
              <label
                key={interest.id}
                className="flex items-center gap-2 text-sm"
                htmlFor={`interest-${interest.id}`}
              >
                <Checkbox
                  id={`interest-${interest.id}`}
                  checked={selected.has(interest.id)}
                  onCheckedChange={(checked) => onToggle(interest.id, checked === true)}
                />
                {interest.name}
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      {retiredButTagged.length > 0 && (
        <fieldset className="grid gap-2">
          <legend className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t('leadForm.retiredInterests')}
          </legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {retiredButTagged.map((interest) => (
              <label
                key={interest.id}
                className="flex items-center gap-2 text-sm"
                htmlFor={`interest-${interest.id}`}
              >
                <Checkbox
                  id={`interest-${interest.id}`}
                  checked={selected.has(interest.id)}
                  onCheckedChange={(checked) => onToggle(interest.id, checked === true)}
                />
                <span className="text-muted-foreground">
                  {interest.categoryName} · {interest.name}
                </span>
              </label>
            ))}
          </div>
          <Label className="text-muted-foreground text-xs font-normal">
            {t('leadForm.retiredInterestsHint')}
          </Label>
        </fieldset>
      )}
    </div>
  )
}
